import { Archive, Pencil, RotateCcw, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@nexus/ui";
import type { AccessTemplateDto } from "@/features/identity-hub/api/identity-hub.api";
import { timeAgo } from "@/features/identity-hub/utils/time";
import { ArchivedBadge } from "./shared/RoleBadges";
import { TableEmptyState } from "./shared/TableEmptyState";
import { RowActionsTrigger } from "./shared/RowActionsTrigger";
import { OrgChips } from "./shared/OrgChips";

interface TemplatesTableProps {
  rows: AccessTemplateDto[];
  onEdit: (row: AccessTemplateDto) => void;
  onChangeStatus?: (row: AccessTemplateDto) => void;
  canEdit?: boolean;
  canChangeStatus?: boolean;
}

export function TemplatesTable({
  rows,
  onEdit,
  onChangeStatus,
  canEdit = true,
  canChangeStatus = true,
}: TemplatesTableProps) {
  const { t } = useTranslation();
  const col = (key: string) => t(`iam.templates.table.${key}`);

  if (rows.length === 0) {
    return (
      <TableEmptyState
        icon={Shield}
        title={t("iam.templates.table.empty.title", "No catalog roles match your filters")}
        subtitle={t("iam.templates.table.empty.subtitle", "Create a role or change the filters.")}
      />
    );
  }

  return (
    <div className="flex min-h-full flex-col overflow-hidden border-t border-border bg-card">
      <table className="w-full text-left text-sm">
        <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground">
          <tr className="border-b border-border">
            <th className="px-3 py-2 font-medium">{col("name")}</th>
            <th className="px-3 py-2 font-medium">{col("scope")}</th>
            <th className="px-3 py-2 font-medium">{col("organizations")}</th>
            <th className="px-3 py-2 font-medium">{col("owner")}</th>
            <th className="px-3 py-2 font-medium">{col("status")}</th>
            <th className="px-3 py-2 font-medium">{col("updated")}</th>
            <th className="w-10 px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="cursor-pointer border-b border-border/70 hover:bg-muted/40"
              onClick={() => onEdit(row)}
            >
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{row.name}</span>
                  {row.status === "ARCHIVED" && <ArchivedBadge />}
                </div>
                {row.description && (
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{row.description}</p>
                )}
              </td>
              <td className="px-3 py-3 text-muted-foreground">
                {row.scopeMode === "WILDCARD"
                  ? t("iam.templates.scopeWildcard", "Wildcard")
                  : t("iam.templates.scopeFixed", "Fixed")}
              </td>
              <td className="px-3 py-3">
                {row.scopeMode === "WILDCARD"
                  ? (
                      <span className="text-muted-foreground">
                        {t("iam.templates.wildcardLabel", "Wildcard")}
                      </span>
                    )
                  : <OrgChips tenants={row.tenants} />}
              </td>
              <td className="px-3 py-3">
                {row.ownerTenant
                  ? <OrgChips tenants={[row.ownerTenant]} />
                  : <span className="text-muted-foreground">{t("iam.templates.table.emptyOwner", "—")}</span>}
              </td>
              <td className="px-3 py-3 text-muted-foreground">
                {row.status === "ARCHIVED"
                  ? t("iam.templates.statusArchived", "Archived")
                  : t("iam.templates.statusActive", "Active")}
              </td>
              <td className="px-3 py-3 text-muted-foreground">{timeAgo(row.updatedAt)}</td>
              <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                {(canEdit || canChangeStatus) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <RowActionsTrigger />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit && (
                        <DropdownMenuItem onClick={() => onEdit(row)}>
                          <Pencil className="h-3.5 w-3.5" />
                          {col("edit")}
                        </DropdownMenuItem>
                      )}
                      {canChangeStatus && onChangeStatus && (
                        <DropdownMenuItem onClick={() => onChangeStatus(row)}>
                          {row.status === "ARCHIVED" ? (
                            <RotateCcw className="h-3.5 w-3.5" />
                          ) : (
                            <Archive className="h-3.5 w-3.5" />
                          )}
                          {row.status === "ARCHIVED" ? col("restore") : col("archive")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
